import { InputBase, styled } from '@mui/material'
import { CellPropsBase, CellTitle, CellValueArea, FormCell } from '.'
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useDebounce } from '../../../../hooks/useDebounce'
import { useNumberParser } from '../../../../hooks/useNumberParser'

type NumberCellProps = {
  value: number | undefined
  onChange?: (v: number | undefined) => void
  placeholder?: string
} & CellPropsBase
export const NumberCell = ({
  label,
  cellWidth,
  value,
  onChange,
  placeholder,
  editable = true,
  required = false,
  edited,
  validationErrors,
}: NumberCellProps) => {
  const [inputValue, setInputValue] = useState('')
  const changedByInput = useRef(false)
  const [isComposing, setIsComposing] = useState(false)

  useEffect(() => {
    changedByInput.current = false
    setInputValue(typeof value === 'number' ? value + '' : '')
  }, [value])

  const { round, parse } = useNumberParser()

  const onChangeInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    changedByInput.current = true
    setInputValue(e.target.value)
  }, [])
  const debouncedInputValue = useDebounce(inputValue, 300)
  useEffect(() => {
    if (isComposing) return
    if (!changedByInput.current) return
    const normalized = parse(debouncedInputValue)
    const newValue = round(normalized)
    onChange && onChange(newValue)
    setInputValue(typeof newValue === 'number' ? newValue + '' : '')
  }, [debouncedInputValue, parse, round, onChange, isComposing])

  const onCompositionStart = useCallback(() => {
    setIsComposing(true)
  }, [])
  const inputValueRef = useRef<string>('')
  useEffect(() => {
    inputValueRef.current = inputValue
  }, [inputValue])
  const onCompositionEnd = useCallback(e => {
    setIsComposing(false)
    changedByInput.current = true
    setInputValue(inputValueRef.current)
  }, [])
  return (
    <FormCell cellWidth={cellWidth}>
      <CellTitle title={label} required={required} />
      <CellValueArea edited={edited} validationErros={validationErrors}>
        <TextInput
          value={inputValue}
          onChange={onChangeInput}
          disabled={!editable}
          placeholder={placeholder || '0'}
          onCompositionStart={onCompositionStart}
          onCompositionEnd={onCompositionEnd}
        />
      </CellValueArea>
    </FormCell>
  )
}

const TextInput = styled(InputBase)({
  width: '100%',
})
